/** Region → General tab. Name, custom_id, card arrangement, countries (remote
 *  multi-select), lat/lng, an optional bounding-box editor, and access tags
 *  (remote multi-select scoped to the region's tenant). Region has no
 *  optimistic-concurrency token, so there is no conflict banner. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { rbacApi, type AccessTag } from '@/api/rbac';
import * as regionsApi from '@/api/regions';
import type { Country, RegionDetail, RegionWriteInput } from '@/api/regions';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { LatLngPicker } from '@/components/LatLngPicker/LatLngPicker';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { usePermissions } from '@/hooks/usePermissions';

import { boundingBoxActions, copyActions, latLngActions } from '../../catalog/catalogLinks';
import { useRegionPatch } from '../hooks/useRegionPatch';

const numOrNull = z.preprocess(
  (v) => (v === '' || v === null || v === undefined ? null : Number(v)),
  z.number({ invalid_type_error: 'Enter a number' }).nullable(),
);

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(256),
  custom_id: z.string().max(256).nullable(),
  card_arrangement: z.enum(['horizontal', 'vertical']),
  latitude: numOrNull.refine((v) => v === null || (v >= -90 && v <= 90), 'Between −90 and 90'),
  longitude: numOrNull.refine((v) => v === null || (v >= -180 && v <= 180), 'Between −180 and 180'),
  bb_north: numOrNull,
  bb_east: numOrNull,
  bb_south: numOrNull,
  bb_west: numOrNull,
});

type FormValues = z.infer<typeof Schema>;

function toDefaults(r: RegionDetail): FormValues {
  const bb = r.bounding_box;
  return {
    name: r.name,
    custom_id: r.custom_id ?? '',
    card_arrangement: r.card_arrangement,
    latitude: r.latitude,
    longitude: r.longitude,
    bb_north: bb?.north ?? null,
    bb_east: bb?.east ?? null,
    bb_south: bb?.south ?? null,
    bb_west: bb?.west ?? null,
  };
}

const ids = (xs: { id: number }[]) => xs.map((x) => x.id).sort((a, b) => a - b);
const sameIds = (a: { id: number }[], b: { id: number }[]) =>
  a.length === b.length && ids(a).every((v, i) => v === ids(b)[i]);

/** A partial bounding box (some corner set, others not) can't be submitted —
 *  the backend wants all four or null. */
function boundingBoxOf(v: FormValues): [number, number, number, number] | null | 'partial' {
  const corners = [v.bb_north, v.bb_east, v.bb_south, v.bb_west];
  const set = corners.filter((c) => c !== null);
  if (set.length === 0) return null;
  if (set.length < 4) return 'partial';
  return [v.bb_north!, v.bb_east!, v.bb_south!, v.bb_west!];
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export function RegionGeneralTab({ region, onSaved }: { region: RegionDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_regions_manage');
  // Access tags are tour-operator-scoped; an unassigned region (null tenant) has
  // no operator to scope them to. Local const so the narrowing flows into the
  // async queryFn closure below.
  const tenantId = region.tenant?.id ?? null;
  const { control, handleSubmit, reset, formState, setError, watch, setValue } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(region),
  });
  const patch = useRegionPatch(region, onSaved);

  // The detail atag arrives as NamedRef (name may be null); the picker deals in
  // AccessTag (name: string). Coerce on seed.
  const seedAtags = (xs: { id: number; name: string | null }[]): AccessTag[] =>
    xs.map((a) => ({ id: a.id, name: a.name ?? '' }));

  const [countries, setCountries] = useState<Country[]>(region.countries);
  const [atags, setAtags] = useState<AccessTag[]>(() => seedAtags(region.atag));

  const resetToRecord = () => {
    reset(toDefaults(region));
    setCountries(region.countries);
    setAtags(seedAtags(region.atag));
  };
  useEffect(() => {
    resetToRecord();
    // Re-seed when the server record changes (incl. after a save → refetch, so the
    // form isn't stuck "dirty"). Region has no updated_ts; a scalar-only save keeps
    // the countries/atag array refs (structural sharing) but yields a fresh top ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  const extrasDirty = useMemo(
    () => !sameIds(countries, region.countries) || !sameIds(atags, region.atag),
    [countries, atags, region.countries, region.atag],
  );

  const submit = handleSubmit((v) => {
    const box = boundingBoxOf(v);
    if (box === 'partial') {
      setError('bb_north', { message: 'Set all four corners, or clear them all.' });
      return;
    }
    const body: RegionWriteInput = {
      name: v.name.trim(),
      custom_id: v.custom_id?.trim() || null,
      card_arrangement: v.card_arrangement,
      countries: countries.map((c) => c.id),
      latitude: v.latitude,
      longitude: v.longitude,
      bounding_box: box,
      atag: atags.map((a) => ({ id: a.id })),
    };
    patch.save(body);
  });

  const err = formState.errors;
  const dirty = formState.isDirty || extrasDirty;
  const lat = watch('latitude');
  const lng = watch('longitude');
  // All four corners or none — a partial box has no centre to open.
  const box = boundingBoxOf(watch());
  const boxValue = box === 'partial' || box === null ? null : box;

  return (
    <form onSubmit={submit}>
      <Section title="Basics">
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Name" error={!!err.name} helperText={err.name?.message} />
        )} />
        <Controller control={control} name="custom_id" render={({ field }) => (
          <TextField {...field} value={field.value ?? ''} size="small" fullWidth sx={{ mb: 1.5 }} disabled={!canManage}
            label="Custom ID" error={!!err.custom_id} helperText={err.custom_id?.message}
            InputProps={{
              endAdornment: (
                <FieldActionsAdornment
                  label="Copy the custom id"
                  actions={field.value ? copyActions(field.value, 'Copy custom id') : []}
                />
              ),
            }} />
        )} />
        <Controller control={control} name="card_arrangement" render={({ field }) => (
          <TextField select size="small" sx={{ mb: 1.5, width: 260 }} disabled={!canManage}
            label="Card arrangement" value={field.value}
            onChange={(e) => field.onChange(e.target.value)}>
            <MenuItem value="horizontal">Horizontal</MenuItem>
            <MenuItem value="vertical">Vertical</MenuItem>
          </TextField>
        )} />
      </Section>

      <Section title="Countries">
        <RemoteAutocomplete<Country>
          label="Countries" size="small" multiple fetchOnEmpty value={countries}
          getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
          queryFn={async (q) => (await regionsApi.listCountries(q)).results}
          onChange={(v) => setCountries((v as Country[]) ?? [])}
        />
      </Section>

      <Section title="Map">
        <Box sx={{ mb: 1.5 }}>
          <LatLngPicker
            size="small"
            disabled={!canManage}
            value={{ lat: watch('latitude'), lng: watch('longitude') }}
            onChange={(v) => {
              setValue('latitude', v.lat, { shouldDirty: true });
              setValue('longitude', v.lng, { shouldDirty: true });
            }}
          />
          <PickerLinks
            items={
              lat != null && lng != null
                ? [{ key: 'latlng', label: `${lat}, ${lng}`, actions: latLngActions({ lat, lng }) }]
                : []
            }
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Bounding box (optional — set all four, or leave all blank to clear)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {(['bb_north', 'bb_east', 'bb_south', 'bb_west'] as const).map((name) => (
            <Controller key={name} control={control} name={name} render={({ field }) => (
              <TextField value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)}
                size="small" sx={{ width: 150 }} disabled={!canManage} inputMode="decimal"
                label={name.slice(3).replace(/^\w/, (c) => c.toUpperCase())}
                error={name === 'bb_north' && !!err.bb_north}
                helperText={name === 'bb_north' ? err.bb_north?.message : undefined} />
            )} />
          ))}
        </Box>
        <PickerLinks
          prefix="Bounding box:"
          items={
            boxValue
              ? [{
                  key: 'bbox',
                  label: 'centre',
                  actions: boundingBoxActions({
                    north: boxValue[0], east: boxValue[1], south: boxValue[2], west: boxValue[3],
                  }),
                }]
              : []
          }
        />
      </Section>

      <Section title="Access">
        {tenantId != null ? (
          <RemoteAutocomplete<AccessTag>
            label="Access tags" size="small" multiple fetchOnEmpty value={atags}
            getOptionLabel={(o) => o.name} getOptionKey={(o) => o.id}
            queryFn={async (q) => (await rbacApi.listAccessTags(tenantId, q)).results}
            onChange={(v) => setAtags((v as AccessTag[]) ?? [])}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Access tags are unavailable — this region has no owning operator.
          </Typography>
        )}
      </Section>

      <EditorSaveBar
        dirty={dirty} saving={patch.saving} canManage={canManage}
        onSave={submit} onDiscard={resetToRecord}
      />
    </form>
  );
}
